/**
 * İkram (Comp) Onayları — müşteri beğenmedi → müdür onaylı bedelden düşme.
 *
 * Garson POS'tan comp talebi açar (PENDING); müdür burada onaylar/reddeder.
 * Onayda: o porsiyon(lar) bedelden düşer + BOM COMPLIMENTARY stoktan düşer
 * (+ opsiyonel remake KDS'e HIGH-priority). Reddedilirse etkisizdir.
 * Comp = suistimal riski #1 → onay yalnız OWNER/MANAGER.
 */
import {
  Badge, Button, Card, Group, Loader, Modal, SegmentedControl,
  Stack, Table, Text, Textarea, Tooltip,
} from "@mantine/core";
import { IconCheck, IconClock, IconX, IconToolsKitchen2 } from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import {
  useLineComps, useApproveLineComp, useRejectLineComp,
} from "../../../shared/api";
import { MoneyDisplay, PageHeader } from "../../../shared/ui";

const STATUS_COLOR: Record<string, string> = {
  PENDING: "orange",
  APPROVED: "green",
  REJECTED: "red",
};
const STATUS_LABEL: Record<string, string> = {
  PENDING: "Onay Bekliyor",
  APPROVED: "Onaylandı",
  REJECTED: "Reddedildi",
};
const REASON_LABEL: Record<string, string> = {
  CUSTOMER_DISSATISFACTION: "Müşteri Beğenmedi",
  KITCHEN_ERROR: "Mutfak Hatası",
  LONG_WAIT: "Uzun Bekleme",
  GOODWILL: "Jest / İyi Niyet",
  OTHER: "Diğer",
};

export function LineCompsPage() {
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const { data, isLoading } = useLineComps(
    statusFilter === "ALL" ? {} : { status: statusFilter },
  );
  const approve = useApproveLineComp();
  const reject = useRejectLineComp();

  const [rejectTarget, setRejectTarget] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejectOpened, { open: openReject, close: closeReject }] = useDisclosure(false);

  const comps = data?.results || data || [];
  const pendingCount = comps.filter((c: any) => c.status === "PENDING").length;

  const handleApprove = async (comp: any) => {
    try {
      const res = await approve.mutateAsync(comp.id);
      notifications.show({
        title: "İkram Onaylandı",
        message: res?.remake_ticket_id
          ? `${comp.product_name} bedelden düşüldü + yeniden hazırlanıyor`
          : `${comp.product_name} bedelden düşüldü`,
        color: "green",
        icon: <IconCheck />,
      });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      await reject.mutateAsync({ id: rejectTarget.id, reason: rejectReason });
      notifications.show({
        title: "Reddedildi",
        message: "İkram talebi iptal edildi — bedel/stok değişmedi",
        color: "gray",
      });
      closeReject();
      setRejectTarget(null);
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  };

  return (
    <Stack gap="md">
      <PageHeader
        title="🎁 İkram (Comp) Onayları"
        subtitle="Müşteri beğenmedi → garson talep açtı; müdür onayı bekler. Onayda bedelden düşülür + stok COMPLIMENTARY."
        actions={
          pendingCount > 0 ? (
            <Badge size="lg" color="orange" leftSection={<IconClock size={14} />}>
              {pendingCount} bekleyen
            </Badge>
          ) : null
        }
      />

      <SegmentedControl
        value={statusFilter}
        onChange={setStatusFilter}
        data={[
          { label: "Bekleyen", value: "PENDING" },
          { label: "Onaylanan", value: "APPROVED" },
          { label: "Reddedilen", value: "REJECTED" },
          { label: "Tümü", value: "ALL" },
        ]}
      />

      {isLoading ? <Loader /> : comps.length === 0 ? (
        <Card padding="xl" radius="md" withBorder>
          <Text c="dimmed" ta="center">
            {statusFilter === "PENDING" ? "Bekleyen ikram onayı yok 🎉" : "Kayıt bulunamadı"}
          </Text>
        </Card>
      ) : (
        <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Ürün</Table.Th>
                <Table.Th ta="right">Porsiyon</Table.Th>
                <Table.Th>Sebep</Table.Th>
                <Table.Th ta="right">Bedel</Table.Th>
                <Table.Th ta="right">Maliyet</Table.Th>
                <Table.Th>Remake</Table.Th>
                <Table.Th>Durum</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {comps.map((c: any) => (
                <Table.Tr key={c.id}>
                  <Table.Td fw={500}>
                    {c.product_name}
                    {c.notes ? (
                      <Text size="xs" c="dimmed">"{c.notes}"</Text>
                    ) : null}
                  </Table.Td>
                  <Table.Td ta="right" fw={600}>{c.comp_quantity}</Table.Td>
                  <Table.Td>
                    <Badge variant="light" color="pink">
                      {REASON_LABEL[c.reason_code] || c.reason_code}
                    </Badge>
                  </Table.Td>
                  <Table.Td ta="right">
                    {c.status === "APPROVED"
                      ? <MoneyDisplay minor={c.amount_minor} />
                      : <Text size="xs" c="dimmed">onayda</Text>}
                  </Table.Td>
                  <Table.Td ta="right">
                    {c.status === "APPROVED"
                      ? <MoneyDisplay minor={c.cost_minor} />
                      : "—"}
                  </Table.Td>
                  <Table.Td>
                    {c.remake_requested ? (
                      <Tooltip label="Müşteri yeniden hazırlanmasını istedi (mutfağa yeni tabak)">
                        <Badge variant="light" color="grape" leftSection={<IconToolsKitchen2 size={12} />}>
                          Yeniden
                        </Badge>
                      </Tooltip>
                    ) : <Text size="xs" c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td>
                    <Badge color={STATUS_COLOR[c.status]}>
                      {STATUS_LABEL[c.status] || c.status}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {c.status === "PENDING" ? (
                      <Group gap="xs">
                        <Button
                          size="xs" color="green"
                          leftSection={<IconCheck size={14} />}
                          onClick={() => handleApprove(c)}
                          loading={approve.isPending}
                        >
                          Onayla
                        </Button>
                        <Button
                          size="xs" color="red" variant="light"
                          leftSection={<IconX size={14} />}
                          onClick={() => {
                            setRejectTarget(c);
                            setRejectReason("");
                            openReject();
                          }}
                        >
                          Reddet
                        </Button>
                      </Group>
                    ) : c.status === "REJECTED" && c.rejection_reason ? (
                      <Text size="xs" c="dimmed">"{c.rejection_reason}"</Text>
                    ) : null}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <Modal opened={rejectOpened} onClose={closeReject} title="İkram Talebini Reddet" centered>
        <Stack>
          <Text size="sm" c="dimmed">
            {rejectTarget?.product_name} ikramı reddedilecek — bedel/stok
            değişmez. Sebep (opsiyonel):
          </Text>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.currentTarget.value)}
            placeholder="Örn. geçerli bir şikâyet değil"
            autosize minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeReject}>Vazgeç</Button>
            <Button color="red" onClick={handleReject} loading={reject.isPending}>
              Reddet
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
